import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiAlertTriangle, FiFileText, FiShield, FiHome, FiArrowLeft } = FiIcons;

const HMSReportTypeSelection = () => {
  const navigate = useNavigate();
  const companyName = sessionStorage.getItem('hmsCompanyName') || 'Bedriften';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm mb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/hms/company-selection')} className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors">
              <SafeIcon icon={FiArrowLeft} />
              <span>Tilbake</span>
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiShield} className="text-white text-sm" />
              </div>
              <span className="font-semibold text-gray-900">HMS Rapportering</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <SafeIcon icon={FiShield} className="text-white text-4xl" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">HMS Rapportering</h1>
          <p className="text-xl text-gray-600">Velg type rapport og bedrift hvor hendelsen fant sted</p>
          <p className="text-sm text-gray-500 mt-2 font-medium bg-blue-50 inline-block px-4 py-1 rounded-full">
            Du er logget inn hos: {companyName}
          </p>
        </div>

        {/* Selection Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* HMS Hendelse Card */}
          <motion.div 
            whileHover={{ y: -5, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
            className="bg-white rounded-2xl p-8 shadow-lg border-2 border-transparent hover:border-red-500 cursor-pointer transition-all group"
            onClick={() => navigate('/hms/incident-form')}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="w-6 h-6 rounded-full border-2 border-gray-300 group-hover:border-red-500 group-hover:bg-red-500 flex items-center justify-center transition-colors">
                <div className="w-2.5 h-2.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <SafeIcon icon={FiAlertTriangle} className="text-red-500 text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">HMS Hendelsesrapport</h3>
            <p className="text-gray-600 leading-relaxed">
              Rapporter HMS-hendelser, ulykker og nestenulykker med full risikovurdering.
            </p>
          </motion.div>

          {/* RUH Card */}
          <motion.div 
            whileHover={{ y: -5, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
            className="bg-white rounded-2xl p-8 shadow-lg border-2 border-transparent hover:border-purple-500 cursor-pointer transition-all group"
            onClick={() => navigate('/hms/ruh-form')}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="w-6 h-6 rounded-full border-2 border-gray-300 group-hover:border-purple-500 group-hover:bg-purple-500 flex items-center justify-center transition-colors">
                <div className="w-2.5 h-2.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <SafeIcon icon={FiFileText} className="text-purple-600 text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">RUH - Rapport Uønsket Hendelse</h3>
            <p className="text-gray-600 leading-relaxed">
              Rapporter trakassering, diskriminering og andre uønskede hendelser.
            </p>
          </motion.div>
        </div>

        {/* Information Boxes */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* HMS Info */}
          <div className="bg-green-50 border border-green-100 rounded-2xl p-8">
            <h4 className="flex items-center gap-2 font-bold text-green-900 text-lg mb-4">
              <SafeIcon icon={FiAlertTriangle} /> Hva er en HMS-hendelse?
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-green-800 text-sm">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Ulykker:</strong> Hendelser som har ført til skade på personer</span>
              </li>
              <li className="flex items-start gap-3 text-green-800 text-sm">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Nestenulykker:</strong> Farlige situasjoner som kunne ført til skade</span>
              </li>
              <li className="flex items-start gap-3 text-green-800 text-sm">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Miljøhendelser:</strong> Utslipp, avfallshåndtering eller forurensning</span>
              </li>
              <li className="flex items-start gap-3 text-green-800 text-sm">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Sikkerhetshendelser:</strong> Brudd på sikkerhetsprosedyrer</span>
              </li>
              <li className="flex items-start gap-3 text-green-800 text-sm">
                <span className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Helsehendelser:</strong> Eksponering for farlige stoffer eller ergonomiske problemer</span>
              </li>
            </ul>
          </div>

          {/* RUH Info */}
          <div className="bg-purple-50 border border-purple-100 rounded-2xl p-8">
            <h4 className="flex items-center gap-2 font-bold text-purple-900 text-lg mb-4">
              <SafeIcon icon={FiFileText} /> Hva er RUH (Rapport Uønsket Hendelse)?
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-purple-800 text-sm">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Trakassering:</strong> Mobbing eller uønsket oppførsel på arbeidsplassen</span>
              </li>
              <li className="flex items-start gap-3 text-purple-800 text-sm">
                <span className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Diskriminering:</strong> Forskjellsbehandling basert på personlige egenskaper</span>
              </li>
              <li className="flex items-start gap-3 text-purple-800 text-sm">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Korrupsjon:</strong> Bestikkelser, underslag eller maktmisbruk</span>
              </li>
              <li className="flex items-start gap-3 text-purple-800 text-sm">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Etiske brudd:</strong> Handlinger som strider mot bedriftens verdier</span>
              </li>
              <li className="flex items-start gap-3 text-purple-800 text-sm">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span><strong>Miljøbrudd:</strong> Brudd på miljøregelverk eller -rutiner</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="text-center mt-12 text-gray-500 text-sm">
          HMS-rapporter behandles konfidensielt og brukes til læring og forbedring av arbeidsmiljøet.
        </div>
      </div>
    </div>
  );
};

export default HMSReportTypeSelection;